import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import { Role, useRole } from 'model/Role';
import { User } from 'model/User';
import { Partner } from 'model/Partner';
import { ResourceParam } from 'model/Resource';
import {
  Ledger,
  LedgerMember,
  ledgerStatusList,
  ledgerPaymentStatusList,
  orderList,
  safetyDocumentTypeList,
  manifestoTypeList,
} from 'model/Ledger';
import { Contractor } from 'model/Contractor';
import { generalStyle } from 'helper/Style';
import { useScroll } from 'helper/Anchor';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import Select from 'component/part/Select';
import SelectUser from 'component/part/SelectUser';
import SelectDate from 'component/part/SelectDate';
import RadioList from 'component/part/RadioList';
import ErrorMessage from 'component/part/ErrorMessage';
import H2 from 'component/part/H2';

const initialLedger: Ledger = {
  id: 0,
  name: '',
  shortName: '',
  startDate: 0,
  endDate: 0,
  summary: '',
  address: '',
  leaderId: 0,
  safetyOfficerId: 0,
  order: 0,
  orderingParty: '',
  primeContractorId: 0,
  price: 0,
  status: 0,
  paymentStatus: 1,
  remarks: '',
  safetyDocumentType: '',
  manifestoType: '',
  members: [],
  resources: [],
  resourceCode: Math.random().toString(32).substring(2),
};

type UploadResourceParam = { file: File, thumbnail?: string };
type UploadedResourceParam = ResourceParam & { delete: boolean };

const resourceUrl = process.env.REACT_APP_RESOURCE_ENDPOINT || '';

const style = {
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(auto, 50rem) auto',
    gap: '4rem',
    width: 'fit-content',
    fontSize: '1.6rem',
  },
  dateArea: {
    display: 'flex',
    gap: '4rem',
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  priceInputArea: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '0.5rem',
  },
  variableListArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
    ['& > div']: {
      display: 'flex',
      gap: '1rem',
    },
  },
  addButton: (deleted = false) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    padding: '0 1rem',
    height: '3rem',
    borderRadius: '0.3rem',
    cursor: 'pointer',
    ...(deleted ? {
      backgroundColor: '#6666aa',
      border: 'solid 0.1rem #444488',
      color: '#ffffff',
      ['&:hover']: {
        backgroundColor: '#8888cc',
      },
    } : {
      backgroundColor: '#eeeeee',
      border: 'solid 0.1rem #aaaaaa',
      ['&:hover']: {
        backgroundColor: '#cccccc',
      },
    }),
  }),
  resourceArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  uploadedResourceArea: (display: boolean) => ({
    display: 'grid',
    transition: 'grid-template-rows 0.3s',
    gridTemplateRows: `2rem ${display ? 1 : 0}fr`,
    ['& > div:nth-of-type(1)']: {
      cursor: 'pointer',
      ['&::after']: {
        content: `"${display ? '▲' : '▼'}"`,
        marginLeft: '0.2rem',
      },
    },
    ['& > div:nth-of-type(2)']: {
      overflowY: 'hidden' as const,
    },
  }),
  resourceItem: (deleted = false) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    border: 'solid 0.1rem #cccccc',
    borderRadius: '0.5rem',
    padding: '1rem',
    ['& > img']: {
      marginLeft: 'auto',
      maxWidth: '10rem',
      maxHeight: '10rem',
    },
    ...(deleted ? {
      backgroundColor: '#ddddff',
    } : {}),
  }),
};

const LedgerEdit: React.FC = () => {
  const api = useApi();
  const { getListAsync: getRoleListAsync } = useRole();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const ledgerId = parseInt(urlParams.id || '');
  const scroll = useScroll({ scrollKey: 'index', smooth: true });
  const today = new Date();
  const [safetyOfficerRole, setSafetyOfficerRole] = useState<Role>();
  const [leaderRole, setLeaderRole] = useState<Role>();
  const [ledger, setLedger] = useState<Ledger>(initialLedger);
  const [startDate, setStartDate] = useState<Date | null>(today);
  const [endDate, setEndDate] = useState<Date | null>();
  const [leaders, setLeaders] = useState<User[]>([]);
  const [safetyOfficers, setSafetyOfficers] = useState<User[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [members, setMembers] = useState<LedgerMember[]>([]);
  const [uploadResources, setUploadResources] = useState<UploadResourceParam[]>([]);
  const [uploadFlowResource, setUploadFlowResource] = useState<UploadResourceParam>();
  const [uploadedResources, setUploadedResources] = useState<UploadedResourceParam[]>([]);
  const [uploadedFlowResource, setUploadedFlowResource] = useState<UploadedResourceParam>();
  const [displayUploadedResources, setDisplayUploadedResources] = useState(false);
  const [error, setError] = useState<{ [key: string]: string | null }>({});
  const leader = leaders.find(({ id }) => id === ledger.leaderId);
  const safetyOfficer = safetyOfficers.find(({ id }) => id === ledger.safetyOfficerId);
  const primeContractor = contractors.find(({ id }) => id === ledger.primeContractorId);
  const safetyDocumentType = safetyDocumentTypeList.find(({ value }) => value === ledger.safetyDocumentType);
  const manifestoType = manifestoTypeList.find(({ value }) => value === ledger.manifestoType);
  const order = orderList.find(({ value }) => value === ledger.order);
  const status = ledgerStatusList.find(({ value }) => value === ledger.status);
  const paymentStatus = ledgerPaymentStatusList.find(({ value }) => value === ledger.paymentStatus);
  const resourceInputRef = useRef<HTMLInputElement>(null);
  const flowResourceInputRef = useRef<HTMLInputElement>(null);
  const partnerCandidates = partners.filter((p) => !members.find((mu) => mu.memberId === p.id));

  const onChangeStartDate = (date: Date | null) => {
    setStartDate(date);
    setError({ ...error, startDate: null });
  };

  const onChangeLedger = <T extends keyof Ledger>(key: T, value: Ledger[T]) => {
    setLedger({ ...ledger, [key]: value });
    setError({ ...error, [key]: null });
  };

  const onAddMember = () => {
    members.push({
      id: -1,
      ledgerId: ledger.id,
      memberId: 0,
      salary: 0,
    });
    setMembers([...members]);
  };

  const onChangeMember = (member: LedgerMember, partner?: Partner | LedgerMember) => {
    if (!partner) {
      return;
    }
    const partnerId = 'memberId' in partner ? partner.memberId : partner.id;
    const anotherMember = members.find(({ memberId }) => memberId === partnerId);
    if (!anotherMember) {
      member.memberId = partnerId;
      member.name = partner.name;
      console.log(member.name, members);
      setMembers([...members]);
    }
  };

  const onChangeMemberSalary = (member: LedgerMember, value: string) => {
    member.salary = parseInt(value);
    setMembers([...members]);
  };

  const onRemoveMember = (index: number) => {
    members.splice(index, 1);
    setMembers([...members]);
  };

  const changeUploadResource = (changedResourceParams: UploadResourceParam[]) => {
    const resourceData = new DataTransfer();
    changedResourceParams.forEach((resourceParam) => {
      resourceData.items.add(resourceParam.file);
    });
    if (resourceInputRef.current) {
      resourceInputRef.current.files = resourceData.files;
    }
    setUploadResources([...changedResourceParams]);
  };

  const onAddUploadResource = (event: React.ChangeEvent<HTMLInputElement>) => {
    const resourceCount = uploadResources.length;
    Array.from(event.target.files || []).forEach((resource, index) => {
      if (!uploadResources.find(({ file: { name } }) => name === resource.name)) {
        uploadResources.push({ file: resource });
        const extension = resource.name.replace(/.*\./, '').toLowerCase();
        if (['png', 'jpg', 'jpeg', 'gif', 'bmp'].includes(extension)) {
          const reader = new FileReader();
          reader.addEventListener('load', (loadedEvent) => {
            const thumbnail = loadedEvent?.target?.result;
            if (thumbnail) {
              uploadResources[resourceCount + index].thumbnail = thumbnail as string;
              setUploadResources([...uploadResources]);
            }
          });
          reader.readAsDataURL(resource);
        }
      }
    });
    changeUploadResource(uploadResources);
  };

  const onDeleteUploadReource = (index: number) => {
    uploadResources.splice(index, 1);
    changeUploadResource(uploadResources);
  };

  const onChangeUploadFlowResource = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFlowResource({ file });
      const extension = file.name.replace(/.*\./, '').toLowerCase();
      if (['png', 'jpg', 'jpeg', 'gif', 'bmp'].includes(extension)) {
        const reader = new FileReader();
        reader.addEventListener('load', (loadedEvent) => {
          const thumbnail = loadedEvent?.target?.result;
          if (thumbnail) {
            setUploadFlowResource({ file, thumbnail: thumbnail as string });
          }
        });
        reader.readAsDataURL(file);
      }
    }
  };

  const onDeleteUploadFlowReource = () => {
    setUploadFlowResource(undefined);
    if (flowResourceInputRef.current) {
      const resourceData = new DataTransfer();
      flowResourceInputRef.current.files = resourceData.files;
    }
  };

  const onDeleteUploadedReource = (index: number) => {
    if (uploadedResources.length > index) {
      uploadedResources[index].delete = !uploadedResources[index].delete;
      setUploadedResources([...uploadedResources]);
    }
  };

  const onDeleteUploadedFlowReource = () => {
    if (uploadedFlowResource) {
      setUploadedFlowResource({ ...uploadedFlowResource, delete: !uploadedFlowResource.delete });
    }
  };

  const onSubmit = async (execute: boolean) => {
    const errors: [string, string][] = [];
    if (!ledger.name) {
      errors.push(['name', '現場名を入力してください。']);
    }
    if (!ledger.shortName) {
      errors.push(['shortName', '現場名略称を入力してください。']);
    }
    if (!ledger.address) {
      errors.push(['address', '現場住所を入力してください。']);
    }
    if (!startDate) {
      errors.push(['startDate', '開始日を設定してください。']);
    }
    if (!ledger.leaderId) {
      errors.push(['leaderId', `${leaderRole?.name}を選択してください。`]);
    }
    if (!ledger.orderingParty) {
      errors.push(['orderingParty', '発注元企業を入力してください。']);
    }
    if (errors.length) {
      errors.forEach(([key, message]) => {
        error[key] = message;
      });
      setError({ ...error });
      scroll(errors[0][0]);

      return false;
    }
    if (!execute) {
      return true;
    }

    ledger.startDate = Math.floor((startDate?.getTime() || 0) / 1000);
    ledger.endDate = Math.floor((endDate?.getTime() || 0) / 1000);
    const resourceParams: { file: File, summary?: string }[] = uploadResources.map(
      (resource) => ({ file: resource.file })
    );
    if (uploadFlowResource) {
      resourceParams.push({ file: uploadFlowResource.file, summary: 'flow' });
    }
    const deleteResources = uploadedResources.flatMap((item) => item.delete ? item.indexName : []);
    if (uploadedFlowResource && (uploadedFlowResource?.delete || uploadFlowResource)) {
      deleteResources.push(uploadedFlowResource.indexName);
    }

    return !!(await api.ledger.update(ledger, members, resourceParams, deleteResources));
  };

  useEffect(() => {
    (async () => {
      if (ledgerId) {
        const ledgerResponse = await api.ledger.get(ledgerId, { with: ['member', 'resource'] });
        if (ledgerResponse) {
          const resources = (ledgerResponse.resources ?? []).filter((item) => item.detail?.summary !== 'flow');
          const flowResource = (ledgerResponse.resources ?? []).find((item) => item.detail?.summary === 'flow');
          setLedger(ledgerResponse);
          ledgerResponse.startDate && setStartDate(new Date(ledgerResponse.startDate * 1000));
          ledgerResponse.endDate && setEndDate(new Date(ledgerResponse.endDate * 1000));
          setMembers((ledgerResponse.members || []).map((member) => ({ ...member, deleted: false })));
          setUploadedResources(resources.map((item) => ({ ...item, delete: false })));
          setUploadedFlowResource(flowResource ? ({ ...flowResource, delete: false }) : undefined);
        } else {
          setLedger({ ...ledger, id: ledgerId });
        }
      }
    })();
  }, [ledgerId]);

  useEffect(() => {
    (async () => {
      const roles = await getRoleListAsync();
      const safetyOfficer = roles.find(({ permissions }) => !!permissions.workManage);
      const leader = roles.find(({ permissions }) => !!permissions.workSupervise);
      const [membersResponse, contractorsResponse, partnersResponse] = await Promise.all([
        api.user.getsByOwn(),
        api.contractor.getsByOwn(),
        api.partner.getsByOwn(),
      ]);
      setSafetyOfficers(membersResponse.filter(({ roleId }) => roleId === safetyOfficer?.id));
      setLeaders(membersResponse.filter(({ roleId }) => roleId === leader?.id));
      setContractors(contractorsResponse);
      setPartners(partnersResponse);
      setSafetyOfficerRole(safetyOfficer);
      setLeaderRole(leader);
    })();
  }, []);

  return <Layout>
    <BackLink link='/ledger/list' label='台帳一覧'></BackLink>
    <H2>台帳作成</H2>
    <div css={style.layout}>
      <div css={generalStyle.form}>
        <FormContent label='現場名' required={true} anchorCode='name'>
          <ErrorMessage message={error.name} />
          <input type="text" value={ledger.name} onChange={(e) => onChangeLedger('name', e.target.value)} />
        </FormContent>
        <FormContent label='現場名略称' required={true} anchorCode='shortName'>
        <ErrorMessage message={error.shortName} />
          <input type="text" value={ledger.shortName} onChange={(e) => onChangeLedger('shortName', e.target.value)} />
        </FormContent>
        <FormContent label='現場住所' required={true} anchorCode='address'>
        <ErrorMessage message={error.address} />
          <textarea value={ledger.address || ''} onChange={(e) => onChangeLedger('address', e.target.value)} />
        </FormContent>
        <div css={style.dateArea}>
          <FormContent label='開始日' required={true} anchorCode='startDate'>
            <ErrorMessage message={error.startDate} />
            <SelectDate
              onChange={onChangeStartDate}
              selected={startDate}
              minDate={today}
            />
          </FormContent>
          <FormContent label='終了日'>
            <SelectDate
              onChange={setEndDate}
              selected={endDate}
              minDate={startDate || today}
            />
          </FormContent>
        </div>
        <FormContent label='請負金額（税抜）'>
          <div css={style.priceInputArea}>
            <input type='number' value={ledger.price} onChange={(e) => onChangeLedger('price', parseInt(e.target.value))} />
            <span>円</span>
          </div>
        </FormContent>
        <FormContent label='工事内容'>
          <textarea value={ledger.summary} onChange={(e) => onChangeLedger('summary', e.target.value)} />
        </FormContent>
        <FormContent label='現場代理人' required={true} anchorCode='leaderId'>
          <ErrorMessage message={error.leaderId} />
          <SelectUser list={leaders} value={leader} onChange={(user) => onChangeLedger('leaderId', user?.id || 0)} />
        </FormContent>
        <FormContent label='上長'>
          <SelectUser list={safetyOfficers} value={safetyOfficer} onChange={(user) => onChangeLedger('safetyOfficerId', user?.id || 0)} />
        </FormContent>
        <FormContent label='発注元企業' required={true} anchorCode='orderingParty'>
          <ErrorMessage message={error.orderingParty} />
          <input type='text' value={ledger.orderingParty || ''} onChange={(e) => onChangeLedger('orderingParty', e.target.value)} />
        </FormContent>
        <FormContent label='安全書類'>
          <Fragment>
            <RadioList list={safetyDocumentTypeList} value={safetyDocumentType} onChange={(item) => onChangeLedger('safetyDocumentType', item.value)} labelKey='label' />
            {ledger.safetyDocumentType === 'other' && (
              <textarea value={ledger.safetyDocumentTypeText || ''} onChange={(e) => onChangeLedger('safetyDocumentTypeText', e.target.value)} />
            )}
          </Fragment>
        </FormContent>
        <FormContent label='マニフェスト'>
          <RadioList list={manifestoTypeList} value={manifestoType} onChange={(item) => onChangeLedger('manifestoType', item.value)} labelKey='label' />
        </FormContent>
        <FormContent label='産業廃棄物フロー図'>
          <Fragment>
            {uploadedFlowResource && (
              <div css={style.resourceArea}>
                <div css={style.resourceItem(uploadedFlowResource.delete || !!uploadFlowResource)}>
                  <button
                    onClick={onDeleteUploadedFlowReource}
                    css={style.addButton(uploadedFlowResource.delete || !!uploadFlowResource)}
                  >ー</button>
                  <div>{uploadedFlowResource.originalName || 'ー'}</div>
                  {uploadedFlowResource.thumbnailName ? (
                    <img src={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${uploadedFlowResource.thumbnailName}`} />
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            )}
            <div css={style.resourceArea}>
              {uploadFlowResource ? (
                <div css={style.resourceItem()}>
                  <button onClick={onDeleteUploadFlowReource} css={style.addButton()}>ー</button>
                  <div>{uploadFlowResource?.file.name || 'ー'}</div>
                  {uploadFlowResource.thumbnail ? (
                    <img src={uploadFlowResource.thumbnail} />
                  ) : (
                    <div />
                  )}
                </div>
              ) : (
                <button onClick={() => flowResourceInputRef.current?.click()} css={style.addButton()}>
                  {uploadedFlowResource ? '＊ファイル差し替え' : '＋ファイル追加'}
                </button>
              )}
              <input
                type='file'
                hidden
                accept='image/*,.png,.jpg,.jpeg,.gif,.pdf'
                onChange={onChangeUploadFlowResource}
                ref={flowResourceInputRef}
              />
            </div>
          </Fragment>
        </FormContent>
        <FormContent label='弊社次数'>
          <RadioList list={orderList} value={order} onChange={(item) => onChangeLedger('order', item.value)} labelKey='label' />
        </FormContent>
        <FormContent label='元請'>
          <Select
            list={contractors}
            value={primeContractor}
            onChange={(primeContractor) => onChangeLedger('primeContractorId', primeContractor?.id || 0)}
            labelKey='name'
          />
        </FormContent>
        {members.map((member, index) => (
          <div css={generalStyle.formRow} key={index}>
            <FormContent label={`請負協力業者${index + 1}`}>
              <div css={style.inputArea}>
                <button onClick={() => onRemoveMember(index)} css={style.addButton()}>ー</button>
                <Select
                  list={[...(member.memberId ? [member] : []), ...partnerCandidates]}
                  value={member.memberId ? member : undefined}
                  labelKey='name'
                  onChange={(value) => onChangeMember(member, value)}
                />
              </div>
            </FormContent>
            <FormContent label='請負金額'>
              <div css={style.inputArea}>
                <input
                  type='number'
                  value={member.salary || 0}
                  onChange={(event) => onChangeMemberSalary(member, event.target.value)}
                />
                円
              </div>
            </FormContent>
          </div>
        ))}
        <button onClick={onAddMember} css={style.addButton()}>＋協力業者追加</button>
        <FormContent label='備考'>
          <textarea value={ledger.remarks} onChange={(e) => onChangeLedger('remarks', e.target.value)} />
        </FormContent>
        {uploadedResources.length > 0 && (
          <FormContent
            label={`アップロード済み添付資料 (${uploadedResources.length})`}
            css={style.uploadedResourceArea(displayUploadedResources)}
            onClick={() => setDisplayUploadedResources(!displayUploadedResources)}
          >
            <div css={style.resourceArea}>
              {uploadedResources.map((resourceParam, index) => (
                <div css={style.resourceItem(resourceParam.delete)} key={index}>
                  <button onClick={() => onDeleteUploadedReource(index)} css={style.addButton(resourceParam.delete)}>ー</button>
                  <div>{resourceParam.originalName || 'ー'}</div>
                  {resourceParam.thumbnailName ? (
                    <img src={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${resourceParam.thumbnailName}`} />
                  ) : (
                    <div />
                  )}
                </div>
              ))}
            </div>
          </FormContent>
        )}
        <FormContent label='添付資料'>
          <div css={style.resourceArea}>
            {uploadResources.map((resourceParam, index) => (
              <div css={style.resourceItem()} key={index}>
                <button onClick={() => onDeleteUploadReource(index)} css={style.addButton()}>ー</button>
                <div>{resourceParam.file.name || 'ー'}</div>
                {resourceParam.thumbnail ? (
                  <img src={resourceParam.thumbnail} />
                ) : (
                  <div />
                )}
              </div>
            ))}
            <input
              type='file'
              multiple
              hidden
              accept='image/*,.png,.jpg,.jpeg,.gif,.pdf'
              onChange={onAddUploadResource}
              ref={resourceInputRef}
            />
            <button onClick={() => resourceInputRef.current?.click()} css={style.addButton()}>＋ファイル追加</button>
          </div>
        </FormContent>
        <OperationTrigger
          onConfirm={() => onSubmit(false)}
          onSubmit={() => onSubmit(true)}
          onOk={() => navigate('/ledger/list')}
          buttonText={ledgerId ? '更新' : '作成'}
          confirmText={`工事台帳を${ledgerId ? '更新' : '登録'}します`}
          submitText={ledgerId ? '更新' : '登録'}
          doneText={`${ledgerId ? '更新' : '登録'}が完了しました`}
          errorText={`${ledgerId ? '更新' : '登録'}に失敗しました`}
        />
      </div>
      <div css={generalStyle.form}>
        <FormContent label='ステータス'>
          <Select list={ledgerStatusList} value={status} labelKey='label' onChange={(item) => onChangeLedger('status', item?.value || 0)} />
        </FormContent>
        <FormContent label='請求ステータス'>
          <Select list={ledgerPaymentStatusList} value={paymentStatus} labelKey='label' onChange={(item) => onChangeLedger('paymentStatus', item?.value || 0)} />
        </FormContent>
      </div>
    </div>
  </Layout>;
};

export default LedgerEdit;
